'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentMethodsRow } from '@/components/checkout/PaymentMethodsRow';
import { EmailTypoHint } from '@/components/ui/email-typo-hint';
import { useParticipant } from '@/lib/participant-auth/context';
import { formatPriceCents } from '@/lib/utils/currency';
import { photoRequestHeaders } from '@/lib/race-photos/client-id';
import { useIsMobile } from '@/hooks/use-mobile';
import { HubSkeleton, hubButtonStyle, hubText } from '@/components/hub/hub-ui';
import { HUB_STRIPE_FONTS, useHubStripeAppearance } from '@/components/hub/races/hub-stripe';
import { StepError, StepHeading, StepText, stepInputStyle, type PhotoVariant } from './photo-flow-ui';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

interface ThemeColors {
  primary: string;
  secondary: string;
  text: string;
  textMuted: string;
  buttonText?: string;
}

interface PhotoPurchaseFormProps {
  albumId: string;
  /**
   * Where the purchase endpoints live. The album page passes nothing and gets
   * `/api/race-photos/<albumId>`; the event's master link passes its own base,
   * because there IS no album in that scope and albumId arrives empty. Without
   * this the master link posted to `/api/race-photos//purchase`, which is not a
   * route, so buying photos through an event link could never succeed.
   */
  apiBase?: string;
  matchedPhotoIds: string[];
  selfieS3Key: string;
  participantPrice: number;
  currency: string;
  /** Kept for callers; the colours come from the v8 tokens around the form. */
  themeColors?: ThemeColors;
  onSuccess: (downloadToken: string) => void;
  /** Kept for callers: both variants draw the same v8 look. */
  variant?: PhotoVariant;
  /**
   * The signed-in athlete's email. With it there is no email field: the
   * receipt and download link go there, and payment opens straight away.
   */
  knownEmail?: string;
}

function formatPrice(cents: number, currency: string) {
  return formatPriceCents(cents, currency);
}

function PurchasePaymentForm({ base, matchedPhotoIds, selfieS3Key, email, amount, currency, onSuccess, onError }: {
  variant?: PhotoVariant;
  base: string;
  matchedPhotoIds: string[];
  selfieS3Key: string;
  email: string;
  amount: number;
  currency: string;
  onSuccess: (downloadToken: string) => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const handlePay = async () => {
    if (!stripe || !elements) return;
    if (!email.trim()) {
      onError('Please enter your email address');
      return;
    }
    setLoading(true);
    // A new attempt clears the last one's message, which otherwise sat over a
    // form the athlete had since corrected.
    onError('');

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError(submitError.message || 'Validation failed');
        setLoading(false);
        return;
      }
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      });

      if (error) {
        onError(error.message || 'Payment failed');
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const res = await fetch(`${base}/purchase/confirm`, {
          method: 'POST',
          headers: photoRequestHeaders(),
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            selfieS3Key,
            matchedPhotoIds,
            email,
          }),
        });

        if (res.ok) {
          const data = await res.json();
          onSuccess(data.downloadToken);
        } else {
          onError('Payment confirmed but failed to generate download access');
        }
      }
    } catch {
      onError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <PaymentMethodsRow
        termsAccepted={!!email.trim()}
        onWalletConfirm={handlePay}
      />
      <button
        type="button"
        onClick={handlePay}
        disabled={!stripe || !elements || loading}
        className={!(!stripe || !elements || loading) ? 'fvd-hoverable-solid' : undefined}
        style={hubButtonStyle('primary', isMobile, { width: '100%', borderRadius: 'var(--fv-radius-button, 10px)', opacity: (!stripe || !elements || loading) ? 0.5 : 1 })}
      >
        {loading ? 'Paying...' : `Pay ${formatPrice(amount, currency)} and download`}
      </button>
    </div>
  );
}

/**
 * Inline purchase form rendered within the photo finder page (not a modal).
 * Collects email and payment, with Stripe in the colours of the page around it.
 */
export function PhotoPurchaseForm({ albumId, apiBase, matchedPhotoIds, selfieS3Key, participantPrice, currency, onSuccess, variant = 'page', knownEmail }: PhotoPurchaseFormProps) {
  const base = apiBase ?? `/api/race-photos/${albumId}`;
  const isMobile = useIsMobile();
  const participant = useParticipant();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [email, setEmail] = useState(knownEmail || participant?.email || '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initiated, setInitiated] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  // The hub's Stripe colours are read where the form sits (hub-stripe.ts).
  const hubStripe = useHubStripeAppearance(isMobile);

  const handleInitiatePurchase = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${base}/purchase`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ selfieS3Key, matchedPhotoIds, email }),
      });

      if (!res.ok) {
        // Surface what the server actually said. This used to throw a fixed
        // "Failed to initiate purchase", which discarded the real reason and
        // left both the buyer and anyone debugging with nothing to go on:
        // "Organiser payment not configured", "These photos are not available
        // yet" and a 429 all looked identical.
        const detail = await res.json().catch(() => null);
        throw new Error(
          detail?.message
            || (res.status === 429
              ? 'Too many attempts. Please wait a moment and try again.'
              : 'Could not start the purchase. Please try again.'),
        );
      }

      const data = await res.json();

      if (data.free) {
        onSuccess(data.downloadToken);
        return;
      }

      setClientSecret(data.clientSecret);
      setInitiated(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Signed in, there is nothing to type: open the payment form straight away.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!knownEmail || autoStarted.current) return;
    autoStarted.current = true;
    void handleInitiatePurchase();
  }, [knownEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stripe in the v8 colours as they resolve where the form sits: the hub's
  // theme (and the HYROX skin) there, the organiser's ground and accent on a
  // public page (PhotoPageSkin). Read off the tokens, not guessed (hub-stripe.ts).
  // Card fields follow the event page's corner style where there is one.
  const appearance = useMemo(() => {
    const a = hubStripe.appearance;
    if (!a || typeof window === 'undefined') return a;
    const radius = getComputedStyle(document.body).getPropertyValue('--fv-radius-control').trim();
    return radius ? { ...a, variables: { ...a.variables, borderRadius: radius } } : a;
  }, [hubStripe.appearance]);
  const frameStyle = hubStripe.frameStyle;

  const showEmailField = !initiated && !knownEmail;
  const count = matchedPhotoIds.length;

  return (
    <div ref={hubStripe.frameRef} className="photo-pay-hub" style={{ width: '100%', maxWidth: 520, ...frameStyle }}>
      <StepHeading variant={variant}>Unlock your photos</StepHeading>
      <StepText variant={variant} style={{ marginBottom: 10 }}>
        {count} photo{count !== 1 ? 's' : ''}, downloaded in full resolution without the watermark.
      </StepText>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 0', borderTop: '1px solid var(--fvd-hairline-light)', borderBottom: '1px solid var(--fvd-hairline-light)', marginBottom: 14 }}>
        <span style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)' }}>Total</span>
        <span style={{ ...hubText.mono, fontSize: 14, color: 'var(--fvd-ink)' }}>{formatPrice(participantPrice, currency)}</span>
      </div>
      {knownEmail && (
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--fvd-ink-muted)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
          The receipt and your download link go to {knownEmail}.
        </p>
      )}

      {/* Email input - shown before Stripe form is loaded */}
      {showEmailField && (
        <div style={{ marginBottom: '16px' }}>
          <label
            htmlFor="photo-receipt-email"
            style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fvd-ink-secondary)', marginBottom: 6 }}
          >
            Email for receipt
          </label>
          <input
            id="photo-receipt-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setEmailFocused(true)}
            onBlur={() => setEmailFocused(false)}
            placeholder="your@email.com"
            style={stepInputStyle(variant, isMobile, {
              // Always set: React removing the longhand on blur wiped the
              // border the shorthand had drawn, and the field lost its edge.
              border: `1px solid ${emailFocused ? 'var(--fvd-teal)' : 'var(--fvd-border-control)'}`,
              outline: 'none',
            })}
          />
          {/* The receipt and the download link both go here. Nothing bounces
              back to the person who typed it. */}
          <EmailTypoHint email={email} onAccept={setEmail} accentColor="var(--fvd-teal-text)" />
        </div>
      )}

      {showEmailField && (
        <button
          type="button"
          onClick={handleInitiatePurchase}
          disabled={loading || !email.trim()}
          className={!(loading || !email.trim()) ? 'fvd-hoverable-solid' : undefined}
          style={hubButtonStyle('primary', isMobile, { width: '100%', marginBottom: 16, borderRadius: 'var(--fv-radius-button, 10px)', opacity: (loading || !email.trim()) ? 0.5 : 1 })}
        >
          {loading ? 'Getting payment ready...' : 'Continue to payment'}
        </button>
      )}

      {error && <StepError variant={variant}>{error}</StepError>}

      {/* Signed in and starting on its own: a retry when that failed, and the
          shape of the card form while it loads. */}
      {knownEmail && !initiated && error && !loading && (
        <button
          type="button"
          onClick={handleInitiatePurchase}
          className="fvd-hoverable"
          style={hubButtonStyle('secondary', isMobile, { borderRadius: 'var(--fv-radius-button, 10px)' })}
        >
          Try again
        </button>
      )}
      {((knownEmail && !initiated && loading) || (clientSecret && !appearance)) && (
        <div aria-busy="true" aria-label="Loading the payment form">
          <HubSkeleton height={44} radius={8} />
          <HubSkeleton height={44} radius={8} style={{ marginTop: 12 }} />
          <HubSkeleton height={44} radius={10} style={{ marginTop: 18 }} />
        </div>
      )}

      {/* PaymentMethodsRow draws its "Or pay with card" divider in Tailwind's
          text-muted, which is the muted SURFACE colour, not a text colour: on
          the v8 canvas it all but disappears. Corrected here. */}
      <style dangerouslySetInnerHTML={{ __html: '.photo-pay-hub .text-muted{color:var(--fvd-ink-muted)}' }} />
      {clientSecret && appearance && (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance, fonts: HUB_STRIPE_FONTS }}>
          <PurchasePaymentForm
            variant={variant}
            base={base}
            matchedPhotoIds={matchedPhotoIds}
            selfieS3Key={selfieS3Key}
            email={email}
            amount={participantPrice}
            currency={currency}
            onSuccess={onSuccess}
            onError={setError}
          />
        </Elements>
      )}
    </div>
  );
}
